import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { getPlusPricing, startPlus } from "~/api/plus"
import type { PlusBillingInterval, PlusPricing } from "~/api/plus"
import { CarouselProgress } from "~/components/CarouselProgress"
import {
  ComingNextCarousel,
} from "~/components/ComingNextCarousel"
import { ComingNextFeatureCard } from "~/components/ComingNextFeatureCard"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { ENTITLEMENTS, hasEntitlement } from "~/utils/entitlements"
import { buildPlusFeatureCards } from "~/utils/plusFeatureCards"
import { trackEvent } from "~/utils/trackEvent"

export function DeeperInsightsPlaceholderPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [pricing, setPricing] = useState<PlusPricing | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const [billingInterval, setBillingInterval] = useState<PlusBillingInterval>("annual")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restoreMessage, setRestoreMessage] = useState<string | null>(null)
  const [retainedDogName, setRetainedDogName] = useState<string | null>(null)
  const [retainedDogSex, setRetainedDogSex] = useState<"male" | "female" | null>(null)
  const carouselRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    Promise.all([getProfile(), getPlusPricing()])
      .then(([profileData, pricingData]) => {
        setProfile(profileData)
        setPricing(pricingData)
        const name = profileData.dog_profile?.name?.trim()
        if (name) setRetainedDogName(name)
        if (profileData.dog_profile?.sex) setRetainedDogSex(profileData.dog_profile.sex)
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      navigate("/dashboard?checkout=success", { replace: true })
    }
  }, [navigate, searchParams])

  const dogName = profile?.dog_profile?.name?.trim() || retainedDogName || "your dog"
  const dogSex = profile?.dog_profile?.sex ?? retainedDogSex
  const isPlusMember = hasEntitlement(profile?.user.entitlements, ENTITLEMENTS.unlimitedAskDynamo)

  const featureCards = useMemo(() => buildPlusFeatureCards(dogName, dogSex), [dogName, dogSex])
  const slideLabels = useMemo(() => featureCards.map((card) => card.title), [featureCards])
  const totalSlides = featureCards.length

  async function handleStartPlus() {
    setSubmitting(true)
    setError(null)
    trackEvent("plus_start_tap", { dogName, billingInterval })
    try {
      const result = await startPlus(billingInterval)
      if (result.mode === "stripe") {
        window.location.href = result.checkout_url
        return
      }
      // Grant responses only return entitlements — refetch full profile so dogs[].plus_locked
      // and subscription stay in sync with account-level Plus.
      try {
        const fresh = await getProfile()
        setProfile(fresh)
      } catch {
        setProfile((prev) => {
          if (!prev) return prev
          const entitlements =
            "user" in result && Array.isArray(result.user?.entitlements)
              ? result.user.entitlements
              : prev.user.entitlements
          const nextDog = "dog_profile" in result && result.dog_profile
            ? result.dog_profile
            : prev.dog_profile
          const name = nextDog?.name?.trim()
          if (name) setRetainedDogName(name)
          if (nextDog?.sex) setRetainedDogSex(nextDog.sex)
          return {
            ...prev,
            ...("subscription" in result && result.subscription
              ? { subscription: result.subscription }
              : {}),
            user: {
              ...prev.user,
              entitlements,
              premium: entitlements.includes(ENTITLEMENTS.unlimitedPhotoUploads),
              plus_welcome_seen_at: prev.user.plus_welcome_seen_at ?? null,
            },
            dog_profile: nextDog ?? prev.dog_profile,
          }
        })
      }
      navigate("/dashboard", { replace: true })
      return
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start Doggy Dynamo Plus")
    } finally {
      setSubmitting(false)
    }
  }

  function handleRestore() {
    if (pricing?.billing_mode === "stripe") {
      setRestoreMessage(
        "Already subscribed? Log in with the account you used at checkout, then open Subscription to manage billing.",
      )
      return
    }
    setRestoreMessage(
      "Staging uses complimentary Plus grants. Ask an admin to run entitlements:grant for your email.",
    )
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <header className="text-center">
          <h1 className="text-3xl font-semibold leading-tight text-[var(--color-ask-dynamo-muted)]">
            Doggy Dynamo Plus
          </h1>
          <p className="mt-1 text-base font-medium text-[var(--color-text-primary)]">
            Built for {dogName}
          </p>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            Deepen your understanding. Strengthen your bond. Enjoy every day together.
          </p>
        </header>

        <ComingNextCarousel
          carouselRef={carouselRef}
          slideCount={totalSlides}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
        >
          {featureCards.map((card) => (
            <ComingNextFeatureCard
              key={card.title}
              title={card.title}
              description={card.tagline}
              icon={card.icon}
              items={card.items}
              footerItems={card.footerItems}
            />
          ))}
        </ComingNextCarousel>

        <CarouselProgress
          total={totalSlides}
          activeIndex={activeIndex}
          labels={slideLabels}
        />

        <div className="mt-5 text-center">
          {isPlusMember ? (
            <div className="rounded-xl border border-[var(--color-ask-dynamo)]/20 bg-[var(--color-ask-dynamo-surface)]/50 p-4">
              <p className="text-sm font-medium text-[var(--color-ask-dynamo-muted)]">
                You’re a Doggy Dynamo Plus member
              </p>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                Built for {dogName} — unlimited Ask Dynamo, pack features, Story, and personalised
                guidance are unlocked.
              </p>
              <Link
                to="/dashboard"
                className="mt-3 inline-flex rounded-lg bg-[var(--color-ask-dynamo)] px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
              >
                Continue to Home
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-[var(--color-text-secondary)]">
                Start building an even stronger relationship with {dogName} today.
              </p>

              <fieldset className="mt-4 space-y-2 text-left">
                <legend className="sr-only">Choose a membership</legend>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[var(--color-ask-dynamo)] bg-[var(--color-ask-dynamo-surface)]/40 p-3">
                  <input
                    type="radio"
                    name="plus-plan"
                    className="mt-1"
                    checked={billingInterval === "annual"}
                    onChange={() => setBillingInterval("annual")}
                  />
                  <span>
                    <span className="flex flex-wrap items-center gap-2 font-semibold text-[var(--color-ask-dynamo-muted)]">
                      ⭐ Annual Membership
                      <span className="rounded-full bg-[var(--color-ask-dynamo)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Best Value
                      </span>
                    </span>
                    <span className="mt-1 block text-sm text-[var(--color-text-primary)]">
                      {pricing?.annual.amount_display ?? "$59.99"} per year
                    </span>
                    <span className="mt-0.5 block text-xs text-[var(--color-text-secondary)]">
                      Just {pricing?.annual.per_month_display ?? "$5.00"}/month when billed annually.{" "}
                      {pricing?.annual.savings_display ?? "Save over 28%."}
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-3">
                  <input
                    type="radio"
                    name="plus-plan"
                    className="mt-1"
                    checked={billingInterval === "monthly"}
                    onChange={() => setBillingInterval("monthly")}
                  />
                  <span>
                    <span className="font-semibold text-[var(--color-text-primary)]">
                      Monthly Membership
                    </span>
                    <span className="mt-1 block text-sm text-[var(--color-text-secondary)]">
                      {pricing?.monthly.amount_display ?? "$6.99"} per month
                    </span>
                  </span>
                </label>
              </fieldset>

              {error && (
                <p className="mt-3 text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={handleStartPlus}
                disabled={submitting}
                className="mt-4 w-full rounded-lg bg-[var(--color-ask-dynamo)] px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? "Starting…" : "Start Doggy Dynamo Plus"}
              </button>

              <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-[var(--color-text-secondary)]">
                <span>Cancel anytime</span>
                <button
                  type="button"
                  onClick={handleRestore}
                  className="underline"
                >
                  Restore purchases
                </button>
              </div>
              {restoreMessage && (
                <p className="mt-2 text-xs text-[var(--color-text-secondary)]" role="status">
                  {restoreMessage}
                </p>
              )}
            </>
          )}
        </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
